import { Component, Input, OnInit } from '@angular/core';

import { CompareValue } from './model';
import { CustomFormComponent } from '@services/formBuilder/abstract-custom-form-field';

import { TranslateService } from '@ngx-translate/core';
import { includes, isEmpty, toString as _toString } from 'lodash';
import { Options } from 'select2';

interface InputData {
  min?: number;
  max?: number;
  id?: string;
}

@Component({
  selector: 'app-compare-value',
  templateUrl: './compare-value.component.html',
  styleUrls: ['./compare-value.component.scss'],
})
export class CompareValueComponent extends CustomFormComponent implements OnInit {
  @Input() inputData: InputData;
  public selectedOption: string;
  public minValue: number;
  public maxValue: number;
  public data = [
    { id: CompareValue.EqualTo, text: this.translate.instant('SHARED.Equal_to') },
    { id: CompareValue.NotEqualTo, text: this.translate.instant('SHARED.Not_equal_to') },
    { id: CompareValue.GreaterThan, text: this.translate.instant('SHARED.Greater_than') },
    { id: CompareValue.LessThan, text: this.translate.instant('SHARED.Less_than') },
    { id: CompareValue.GreaterThanEqualLessThanEqual, text: this.translate.instant('SHARED.Greater_than_equal_Less_than_equal') },
    { id: CompareValue.GreaterThanLessThan, text: this.translate.instant('SHARED.Greater_than_Less_than') }
  ];
  public isRange: boolean;
  public options: Options = {
    minimumResultsForSearch: Infinity,
    placeholder: this.translate.instant('DASHPAGES.CHANGING_TYPE_TS_placeholder'),
    sorter: (data) => data,
    dropdownAutoWidth: true
  };

  constructor(private translate: TranslateService) {
    super();
  }

  public ngOnInit() {
    this.selectedOption = this.inputData?.id || this.formValue?.id || CompareValue.EqualTo;
    this.minValue = this.inputData?.min ?? this.formValue?.min;
    this.maxValue = this.inputData?.max ?? this.formValue?.max;

    this.defineRangeOptions();
  }

  public onTypeChange(selection: { value: string }) {
    this.selectedOption = selection.value;
    this.defineRangeOptions();
    this.emitChanges();
  }

  public onValueChange(value: number, isMin = true) {
    if (isMin) {
      this.minValue = value;
    } else {
      this.maxValue = value;
    }

    this.emitChanges();
  }

  public blockIfInvalid(event: InputEvent): void {
    const currentValue = this.getCurrentValueFromEvent(event);
    const validNumberRegex = /^-?\d*(\.\d*)?$/;

    if (!validNumberRegex.test(currentValue)) {
      event.preventDefault();
    }
  }

  public validateAndCorrect(control: 'min' | 'max' = 'min') {
    const validNumberRegex = /^-?\d+(\.\d+)?$/;

    if (control === 'min') {
      if (!validNumberRegex.test(_toString(this.minValue))) {
        this.minValue = (this.isRange ? this.maxValue : 0) ?? 0;
      }
    } else {
      if (!validNumberRegex.test(_toString(this.maxValue))) {
        this.maxValue = (this.isRange ? this.minValue : 0) ?? 0;
      }
    }

    if (this.isRange) {
      if (control === 'min') {
        if (+this.minValue > +this.maxValue) {
          this.minValue = this.maxValue ?? 0;
        }
      } else {
        if (+this.maxValue < +this.minValue) {
          this.maxValue = this.minValue ?? 0;
        }
      }
    }

    this.emitChanges();
  }

  private emitChanges(): void {
    if (!this.selectedOption) {
      return;
    }

    if (this.isRange && !isEmpty(this.minValue) && !isEmpty(this.maxValue)) {
      this.formValue = { id: this.selectedOption, min: this.minValue, max: this.maxValue };
    } else if (!isEmpty(this.minValue)) {
      this.formValue = { id: this.selectedOption, min: this.minValue };
    } else {
      this.formValue = null;
    }
  }

  private defineRangeOptions() {
    this.isRange = includes([
      CompareValue.GreaterThanEqualLessThanEqual,
      CompareValue.GreaterThanLessThan
    ], this.selectedOption);
  }

  private getCurrentValueFromEvent(event: InputEvent): string {
    const inputElement = event.target as HTMLInputElement;
    const value: string = inputElement.value;
    const selectionStart: number = inputElement.selectionStart ?? 0;
    const selectionEnd: number = inputElement.selectionEnd ?? 0;
    const data: string = event.data;
    let futureValue: string;

    if (data === null) {
      // Handle deletion (Backspace/Delete)
      if (selectionStart !== selectionEnd) {
        futureValue = value.slice(0, selectionStart) + value.slice(selectionEnd);
      } else {
        if (event.inputType === 'deleteContentBackward') {
          futureValue = value.slice(0, selectionStart - 1) + value.slice(selectionEnd);
        } else if (event.inputType === 'deleteContentForward') {
          futureValue = value.slice(0, selectionStart) + value.slice(selectionEnd + 1);
        } else {
          futureValue = value; // Other deletion types (cut etc.)
        }
      }
    } else {
      // Insertion or replacement
      futureValue = value.slice(0, selectionStart) + data + value.slice(selectionEnd);
    }

    return futureValue;
  }
}
